import React, { useEffect, useState, useRef, ChangeEvent, useMemo, useCallback, SyntheticEvent } from 'react';
import {
  OrdersApi,
  Order as FlipdishOrder
} from '@flipdish/api-client-typescript';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  updateOrderStatus,
  acceptOrder,
  rejectOrder,
  refundOrder,
  resetOrder,
  addNoteToOrder,
  getOrder,
  resetAndResendOrder,
  updateOrder,
  fetchOrderHistory,
} from 'actions/orderActions';
import { newNotification } from '../../actions/uiActions';
import {
  Grid,
  Button,
  Divider,
  Typography,
  TextField,
  CircularProgress,
  Paper,
  Modal,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import {
  orderStatuses,
  orderStatusSequence
} from 'constants/orderConstants';
import { red, green } from '@mui/material/colors';

import {
  completeMissionAirspace,
  forceOrderEircodeThunk
} from 'actions/devActions';
import {
  selectIsFetchingCompleteMission,
  selectIsFetchingForceEircodeStatus
} from 'selectors/devSelectors';
import { rerouteOrderThunk } from 'actions/adminActions';
import {
  selectCells,
  selectOrderDestinationLandingPad,
  selectOrderDetails,
  selectOrderOriginLandingPad,
  selectSelectedOrder,
  selectSelectedOrderId,
  selectSelectedOrderHistory,
  selectIsFetchingOrderHistory,
} from 'selectors/ordersSelector';
import { selectOverweightColorPairingsForOrder } from 'selectors/ordersSelector';
import { selectPointOfSales } from 'selectors/adminSelector';
import { useAppSelector } from 'hooks/redux';
import { AddressFind, Cell, DropLocation, Order, PublicOrder, PlaceContext, CreateLocationNoteRequest, LocationNote } from '@m4a3/api-client';
import { getLoggedInUserName } from '../../services/amplify-auth';
import { createDateTimeFromIso, defaultTimezone, removeSecondsFromIsoString, tsToLocalDateString, tsToTimeDay, convertUtcTimeStringForTimezone, convertUtcDateStringForTimezone, droneDeliveryDurationMinutes, isoStringToDateTimeObject, shortDateStringOptions } from '../../utils/date-utils';
import { DateTime } from 'luxon';
import { getShortIdForOrder, isExtraDeliveryOrder, hasRouteForOrder, orderHasPadsAssigned, isDeliverySpotSet, getElementForDropPointStatus } from '../../utils/orderUtils';
import { getRequiredActionsForOrder } from '../../utils/getRequiredActions';
import { Circle, MapContainer, Pane, TileLayer } from 'react-leaflet';
import { dropPointRadiusInMetres } from '../../constants';
import { logToDatadog } from '../../services/datadog';
import { NOT_IN_PROGRESS_STATUSES } from '../../utils/orderUtils';
import MapController from '../../components/MapController';
import { getDescriptionForCollectorStatus, getEmojiForCollectorStatus } from './types/CollectorStatus';
import OrderBasketItem from './components/OrderBasketItem';
import OverdueTimer from './components/OverdueTimer';
import HeaderTimer from './components/HeaderTimer';
import ETADialog from './components/ETADialog';
import PadSelector from './components/PadSelector';
import ActionConfirmationModal from './components/ActionConfirmationModal';
import ResendConfirmationModal from './components/ResendConfirmationModal';
import ResetConfirmationModal from './components/ResetConfirmationModal';
import RejectCancelDialog from './components/RejectCancelDialog';
import { OrderConfirmableAction, OrderConfirmableActionType, ORDER_CONFIRMABLE_ACTION_COPY } from './types/OrderActionTypes';
import { DeprecatedOrderCard } from './components/DeprecatedOrderCard';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { getLocationColor } from '../../utils/locationColors';
import { usePopupState, bindTrigger, bindMenu } from 'material-ui-popup-state/hooks';
import { Box } from '@mui/material';

import { orderfulApi, deliveryApi } from '../../services/api-instances';

// Import Order column icons
import orderDetailsStatusIcon from '../../assets/svgs/orderDetailsStatusIcon.svg';
import orderDetailsExpectedForIcon from '../../assets/svgs/orderDetailsExpectedForIcon.svg';
import orderDetailsCollectorIdIcon from '../../assets/svgs/orderDetailsCollectorIdIcon.svg';
import orderDetailsWeightIcon from '../../assets/svgs/orderDetailsWeightIcon.svg';
import orderDetailsLinkIcon from '../../assets/svgs/orderDetailsLinkIcon.svg';

import orderDetailsEircodeIcon from '../../assets/svgs/orderDetailsEircodeIcon.svg';
import orderDetailsNewNoteIcon from '../../assets/svgs/orderDetailsNewNoteIcon.svg';

// Import Other section icons
import refundIcon from '../../assets/svgs/refund.svg';
import resetIcon from '../../assets/svgs/reset.svg';
import resetResendIcon from '../../assets/svgs/resetResend.svg';
import confirmRouteIcon from '../../assets/svgs/confirmRoute.svg';
import rerouteIcon from '../../assets/svgs/reroute.svg';
import extraDeliveryIcon from '../../assets/svgs/extraDelivery.svg';

// Import Required section action icons
import confirmRouteActionIcon from '../../assets/svgs/confirmRouteActionIcon.svg';
import assignPadActionIcon from '../../assets/svgs/assignPadActionIcon.svg';
import suggestDeliverySpotActionIcon from '../../assets/svgs/suggestDeliverySpotActionIcon.svg';

// Import Provider logos
import MannaProviderLogo from '../../assets/images/MannaProviderLogo.png';
import JustEatProviderLogo from '../../assets/images/JustEatProviderLogo.png';
import DeliverooProviderLogo from '../../assets/images/DeliverooProviderLogo.png';
import DoorDashProviderLogo from '../../assets/images/DoorDashProviderLogo.png';
import WoltProviderLogo from '../../assets/images/WoltProviderLogo.png';
import PlaceDropPointMap from 'components/PlaceDropPointSelector/PlaceDropPointMap';
import PlaceNotes from 'components/PlaceDropPointSelector/PlaceNotes';
import { RoutePanel } from '../../components/RoutePanel/RoutePanel';
import { OrderCardContext } from './components/OrderCardContext';
import { getAllActivePadsForOrder } from '../../utils/landingPadUtils';
import { selectPads } from '../../selectors/liveAircraftSelectors';
import { inactiveStatuses } from '../../constants';
import { OrderActivityTimeline } from './components/OrderActivityTimeline';
import { ExpectedTimetableConnected } from './components/ExpectedTimetable';

const ordersApi = new OrdersApi();
ordersApi.accessToken = 'e2d6bd2c866a89c5ac4c1fafd2be727f';

// Pre-generated notes for quick selection - defined at module level to avoid recreation
const PRE_GENERATED_NOTES = [
  { id: 'delay-driver', text: 'Delay: Driver' },
  { id: 'delay-collector', text: 'Delay: Collector' },
  { id: 'delay-vendor', text: 'Delay: Vendor' },
  { id: 'delay-airspace', text: 'Delay: Airspace' },
  { id: 'cassette-issue', text: 'Cassette Issue' },
  { id: 'hardware-issue', text: 'Hardware Issue' },
  { id: 'collector-device-issue', text: 'Collector device issue' },
  { id: 'software-issue', text: 'Software issue' },
  { id: 'tech-issue', text: 'Tech issue' },
  { id: 'support-staff-issue', text: 'Support staff issue' },
];

type DeprecatedOrderCardPropsType = {
  addressFind?: AddressFind;
  isBackGardenDelivery?: boolean;
  onClickShowSuggestedDropModalForOrder?: (orderId: string) => void;
  onClickInvalidateDropLocation?: (orderId: string, locationId: string) => void;
  updateLandingZoneForOrder?: (orderId: string, landingZoneId: string) => void;
  landingZones?: any[];
  pads: any[];
};

type OrderDetailsProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps> &  {
  onOpenRouteConfirmation: ((order: Order) => void);
  openSuggestModal: (() => void);
  deprecatedOrderCardProps?: DeprecatedOrderCardPropsType;
};

const OrderDetails = ({
  setOrderStatus,
  resetOrder,
  addNote,
  acceptOrder,
  acceptingOrderWithEta,
  rejectOrder,
  rejectingOrder,
  refundOrder,
  refundingOrder,
  resendOrder,
  notify,
  updateOrder,
  onOpenRouteConfirmation,
  openSuggestModal,
  deprecatedOrderCardProps,
}: OrderDetailsProps) => {
  const [fdOrder, setFDOrder] = useState<FlipdishOrder>();
  const [note, setNote] = useState('');
  const [notesAnchorEl, setNotesAnchorEl] = useState<null | HTMLElement>(null);


  const [actionToConfirm, setActionToConfirm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newEircode, setNewEircode] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [customerPhoneNumber, setCustomerPhoneNumber] = useState<string | null>(null);
  const [isFetchingPhone, setIsFetchingPhone] = useState(false);
  const [hasRevealedPhone, setHasRevealedPhone] = useState(false);
  const [orderDropLocation, setOrderDropLocation] = useState<DropLocation>();
  const [placeContext, setPlaceContext] = useState<PlaceContext | null>(null);
  const [dropPointRiskAssessment, setDropPointRiskAssessment] = useState<any>(null);
  // Note management state
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState<LocationNote['noteType']>('general');
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [isTogglingPlaceStatus, setIsTogglingPlaceStatus] = useState(false);
  const [showPadSelector, setShowPadSelector] = useState(false);
  const [confirmModalState, setConfirmModalState] = useState<{
    type: OrderConfirmableActionType | null;
    open: boolean;
  }>({ type: null, open: false });
  const [showResendModal, setShowResendModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showAdvancedActions, setShowAdvancedActions] = useState(false);
  const [orderIdDropdownAnchor, setOrderIdDropdownAnchor] = useState<null | HTMLElement>(null);
  const [showDeprecatedCard, setShowDeprecatedCard] = useState(false);
  const [isEditingExpectedTime, setIsEditingExpectedTime] = useState(false);
  const [showExpectedTimetableModal, setShowExpectedTimetableModal] = useState(false);
  const isCompletingMission = useSelector(selectIsFetchingCompleteMission);
  
  const popupStateLandingZone = usePopupState({
    variant: 'popover',
    popupId: 'landing-zone-order-details'
  });

  const popupStateDeparturePad = usePopupState({
    variant: 'popover',
    popupId: 'departure-pad-order-details'
  });

  const popupStateArrivalPad = usePopupState({
    variant: 'popover',
    popupId: 'arrival-pad-order-details'
  });
  
  const selectedOrderId = useSelector(selectSelectedOrderId);
  const selectedOrder = useAppSelector(selectSelectedOrder);
  const orderDetails = useAppSelector(selectOrderDetails);
  const orderHistory = useSelector(selectSelectedOrderHistory);
  const isFetchingOrderHistory = useSelector(selectIsFetchingOrderHistory);
  const expectedOrderTime = useAppSelector((s) =>
    selectedOrderId ? s.orders.latestExpectedOrderTimeByOrderId?.[selectedOrderId] : undefined
  );
  const isFetchingExpectedOrderTime = useAppSelector((s) =>
    selectedOrderId ? !!s.orders.isFetchingLatestExpectedOrderTimeByOrderId?.[selectedOrderId] : false
  );
  const currentOrderIdRef = useRef();
  const currentOrderStatusRef = useRef();
  const expectedDeliveryDateRef = useRef<string>();
  const cells = useSelector(selectCells);
  const pads = useAppSelector(selectPads);
  
  // orderDetails: Fetched from detailed API endpoint (/order/{id}) - has full basket/items but missing real-time properties
  // selectedOrder: From SSE/polling feed - has all real-time properties (landingZoneName, collectorStatus, teams, etc.)
  // Use orderDetails for basket/items display, but use selectedOrder for real-time data like landing zones
  const order = orderDetails ?? selectedOrder;
  const isDeliverooOrder =
    order?.externalProviderName === 'DELIVEROO';
  const canCancelViaRefundAction = Boolean(fdOrder) || isDeliverooOrder;
  const refundActionLabel = isDeliverooOrder ? 'Cancel' : 'Refund';
  const originLandingPad = useAppSelector((state) => selectOrderOriginLandingPad(state, order?.originLandingPadId));
  const destinationLandingPad = useAppSelector((state) => selectOrderDestinationLandingPad(state, order?.destinationLandingPadId));
  const shortOrderId = getShortIdForOrder(selectedOrder ?? order);
  const pointOfSales = useSelector(selectPointOfSales);
  const pos = React.useMemo(() => {
    try {
      const fdId = (order as any)?.externalProviderId
        ? parseInt((order as any)?.externalProviderId)
        : undefined;
      return pointOfSales?.find(
        (p: any) => p?.id === (order as any)?.posId || (fdId && p?.fdStoreId === fdId)
      );
    } catch (_e) {
      return undefined;
    }
  }, [pointOfSales, order]);

  // Linked orders for overweight grouping (color pairings)
  const linkedOrdersForOverweightOrders = useAppSelector(state =>
    order?.orderShortId ? selectOverweightColorPairingsForOrder(state as any, order.orderShortId) : null
  );

  const isFetchingForceEircode = useSelector(
    selectIsFetchingForceEircodeStatus
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      currentOrderIdRef.current !== selectedOrderId ||
      currentOrderStatusRef.current !== order?.status
    ) {
      setOrderDropLocation(null);
      setCustomerPhoneNumber(null);
      setIsFetchingPhone(false);
      setHasRevealedPhone(false);
      setFDOrder(undefined);
      currentOrderIdRef.current = selectedOrderId;
      currentOrderStatusRef.current = order?.status;
      if (selectedOrderId) {
        getOrderAndData();
        dispatch(fetchOrderHistory(selectedOrderId));
      }

      if (order?.eircode) {
        deliveryApi.getDropLocationInfo(order.eircode)
          .then(addressFind => {
            setOrderDropLocation(addressFind.dropLocation); // this should automatically use the preferred drop location
          })
      }
    }
  }, [order?.externalOrderId, selectedOrderId, order?.status, order?.externalProviderName, order?.mission]);

  // Load PlaceContext for delivery notes when Eircode changes or order is updated
  // Including modifyDate ensures we refresh after drop point changes
  useEffect(() => {
    let isCancelled = false;
    const fetchContext = async () => {
      if (!order?.eircode) {
        setPlaceContext(null);
        return;
      }
      try {
        const context = await orderfulApi.getPlaceContextByEircode(order.eircode.trim().toUpperCase());
        if (!isCancelled) {
          setPlaceContext(context);
        }
      } catch (_e) {
        if (!isCancelled) {
          setPlaceContext(null);
        }
      } 
    };
    fetchContext();
    return () => {
      isCancelled = true;
    };
  }, [order?.eircode, order?.modifyDate]);

  // Fetch risk assessment for preferred drop point
  useEffect(() => {
    let isCancelled = false;
    const fetchRiskAssessment = async () => {
      if (!placeContext) {
        setDropPointRiskAssessment(null);
        return;
      }
      
      const preferredId = placeContext.place.preferredDropPointId;
      const preferred = (placeContext.dropPoints?.data || []).find((d: any) => d.id === preferredId && !d.isDeleted);
      
      if (!preferred) {
        setDropPointRiskAssessment(null);
        return;
      }
      
      try {
        const riskResponse = await orderfulApi.evaluateDropPointRisk({
          latitude: preferred.latitude,
          longitude: preferred.longitude,
          radius: 3.0, // MAP_CONFIG.RISK_ASSESSMENT_RADIUS
          cellId: 7, // MAP_CONFIG.DEFAULT_CELL_ID
        });
        
        if (!isCancelled) {
          const assessment = {
            groundRisk: riskResponse.heightData?.height !== null && riskResponse.heightData?.height >= 1.0 ? 'HIGH' : 'LOW',
            buildingRisk: riskResponse.isNearBuilding ? 'HIGH' : 'LOW',
            heightData: riskResponse.heightData?.height || undefined,
          };
          setDropPointRiskAssessment(assessment);
        }
      } catch (error) {
        console.error('Risk assessment failed:', error);
        if (!isCancelled) {
          setDropPointRiskAssessment({ groundRisk: 'ERROR', buildingRisk: 'ERROR', hasError: true });
        }
      }
    };
    
    fetchRiskAssessment();
    return () => {
      isCancelled = true;
    };
  }, [placeContext]);

  // Note management handlers
  const handleSaveNote = useCallback(async () => {
    if (!placeContext || !noteText.trim()) {
      setNoteError('Note text is required');
      return;
    }

    setIsSavingNote(true);
    setNoteError(null);

    try {
      const createdBy =
        (await getLoggedInUserName().catch(() => undefined)) || 'unknown_user';

      const noteData: CreateLocationNoteRequest = {
        noteText: noteText.trim(),
        noteType: noteType,
        createdBy,
      };

      await orderfulApi.addPlaceNote(placeContext.placeId, noteData);
      
      // Reset form
      setNoteText('');
      setNoteType('general');
      setIsAddingNote(false);
      
      // Refresh place context
      if (order?.eircode) {
        const refreshedContext = await orderfulApi.getPlaceContextByEircode(order.eircode.trim().toUpperCase());
        setPlaceContext(refreshedContext);
      }
      
    } catch (error) {
      console.error('Failed to create note:', error);
      setNoteError('Failed to create note. Please try again.');
    } finally {
      setIsSavingNote(false);
    }
  }, [placeContext, noteText, noteType, order?.eircode]);

  const handleCancelNote = useCallback(() => {
    setNoteText('');
    setNoteType('general');
    setIsAddingNote(false);
    setNoteError(null);
  }, []);

  // Handle place status toggle (enable/disable address)
  const handleTogglePlaceStatus = useCallback(async () => {
    if (!placeContext) return;
    
    const newEnabled = !placeContext.place.enabled;
    
    try {
      setIsTogglingPlaceStatus(true);
      await orderfulApi.updatePlaceContext(placeContext.placeId, { enabled: newEnabled });
      
      // Refresh place context
      if (order?.eircode) {
        const refreshedContext = await orderfulApi.getPlaceContextByEircode(order.eircode.trim().toUpperCase());
        setPlaceContext(refreshedContext);
      }
      
      notify({ variant: 'success', message: `Address ${newEnabled ? 'enabled' : 'disabled'} successfully` });
    } catch (error) {
      notify({ variant: 'error', message: `Failed to ${newEnabled ? 'enable' : 'disable'} address` });
    } finally {
      setIsTogglingPlaceStatus(false);
    }
  }, [placeContext, order?.eircode]);

  // Close status dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showStatusDropdown) {
        const target = event.target as HTMLElement;
        const statusButton = target.closest('[data-status-button]');
        const statusDropdown = target.closest('[data-status-dropdown]');
        
        if (!statusButton && !statusDropdown) {
          setShowStatusDropdown(false);
        }
      }
    };

    if (showStatusDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusDropdown]);

  const handleExtraDelivery = async () => {

    if (!order || !order?.id) { 
      notify({ variant: 'error', message: 'Failed to create extra delivery - missing order Id' });
      return 
    }

    try {
      await orderfulApi.createExtraDeliveryOrder(order.id);
      notify({ variant: 'success', message: 'Successfully created extra delivery' });
    } catch (err) {
      notify({ variant: 'error', message: 'Failed to create extra delivery' });
    }
  };

  /**
   * 
   * THis is purely a UX solution.
   * 
   * This button appears to unlink and remove the order, however it just resets the order. When linking orders
   * we ignore reset orders, so this will appear to also unlink the order with existing "in progress" orders. 
   * 
   */
  const handleRemoveExtraDelivery = async () => {

    if (!order) {
      notify({ variant: 'error', message: 'Failed to remove extra delivery - Try to reset the order instead' });
    }

    handleReset();
  };

  const updateNewEircode = event => {
    setNewEircode(event.target.value);
  };

  const getFDOrderData = (order: any) => {

    return ordersApi
      .getOrderById(order?.externalOrderId)
      .then(res => {
        if (res.body.Data.AcceptedFor) {
          expectedDeliveryDateRef.current = res.body.Data.AcceptedFor.toISOString();
        }
        if (currentOrderIdRef.current === order?.id) {
        setFDOrder(res.body.Data);
        }
      })
      .catch(e => {
        if (currentOrderIdRef.current === order?.id) {
        setFDOrder(null);
        }
        notify({
          variant: 'error',
          message: 'Failed to load flipdish order data'
        });
      });
  };

  const getOrderAndData = async () => {

    const fullOrder = await dispatch(getOrder(selectedOrderId)) as unknown as Order;

    if (fullOrder?.externalOrderId && fullOrder?.externalProviderName === "FLIPDISH") {
      getFDOrderData(fullOrder);
    } else {
      setFDOrder(null);
    }
  }



  const getDecryptedPhoneNumber = async (orderId: string) => {
    try {
      setIsFetchingPhone(true);
      const publicOrder = await orderfulApi.getOrderById(orderId, {
        shouldDecryptNumber: true
      }) as unknown as PublicOrder;
      setCustomerPhoneNumber(publicOrder.customerPhoneNumber);
    } catch (_e) {
      notify({ variant: 'error', message: 'Failed to fetch phone number' });
    } finally {
      setIsFetchingPhone(false);
    }
  };

  const getCustomerPhoneNumber = (): string => {
    if (fdOrder) {
      return fdOrder?.Customer?.PhoneNumber;
    }

    return customerPhoneNumber as string;
  };

  const onClickPhoneNumber = async () => {
    // First click reveals, subsequent click copies to clipboard
    if (hasRevealedPhone && getCustomerPhoneNumber()) {
      try {
        await navigator.clipboard.writeText(getCustomerPhoneNumber());
        notify({ variant: 'success', message: 'Phone number copied to clipboard!' });
      } catch (_e) {
        notify({ variant: 'error', message: 'Failed to copy to clipboard' });
      }
      return;
    }

    setHasRevealedPhone(true);
    if (!getCustomerPhoneNumber() && order?.id) {
      // Decide source based on provider: Flipdish vs decrypt
      if ((order as any)?.externalProviderName === 'FLIPDISH') {
        try {
          setIsFetchingPhone(true);
          await getFDOrderData(order);
        } finally {
          setIsFetchingPhone(false);
        }
      } else {
        await getDecryptedPhoneNumber(order.id);
      }
    }
  };

  const handleAddNote = () => {
    if (note !== null && note !== '') {
      addNote(note, order?.id);
      setNote('');
    } else {
      notify({ variant: 'error', message: 'Note cannot be empty' });
    }
  };

  const getTimezoneForOrderCell = (order: Order): string => {
    const orderCellId = order?.cellId;
    const matchingCell: Cell = cells?.find((c: Cell) => c.id === orderCellId);
    return matchingCell?.timezone || defaultTimezone;
  };

  const getTakeOffTimeForOrder = (expectedDeliveryDate: string): string => {
    if (!expectedDeliveryDate) return '';
    try {
      const expectedTakeoffTime = isoStringToDateTimeObject(expectedDeliveryDate, getTimezoneForOrderCell(selectedOrder || order))
        .minus({minutes: droneDeliveryDurationMinutes})
        .toJSDate()
        .toISOString();
      return convertUtcTimeStringForTimezone(expectedTakeoffTime, getTimezoneForOrderCell(selectedOrder || order));
    } catch {
      return '';
    }
  };

  const getDateForOrderSlot = (expectedDeliveryDate: string): string => {
    if (!expectedDeliveryDate) return '';
    try {
      return convertUtcDateStringForTimezone(expectedDeliveryDate, getTimezoneForOrderCell(selectedOrder || order), shortDateStringOptions);
    } catch {
      return '';
    }
  };

  const acceptedOrder = (delayInMinutes: number) => {
    if (delayInMinutes) {
      const newExpectedDeliveryDate = DateTime.fromISO(order.expectedDeliveryDate).plus({minutes: delayInMinutes}).toJSDate().toISOString();
      const updatedOrder: Order = {
        ...order,
        expectedDeliveryDate: newExpectedDeliveryDate
      };

      logToDatadog('update_order accepted_order', {updateOrder, order, newExpectedDeliveryDate});

      updateOrder(updatedOrder);
    }

    acceptOrder(order, delayInMinutes);
  };

  const handleReset = (reason?: string) => {
    resetOrder(order, reason);
  };

  const handleResend = (reason: string, shouldRemakeFood: boolean) => {
    resendOrder(order, reason, shouldRemakeFood);
  };

  const handleAbort = () => {
    setOrderStatus(order, 'ABORTED');
  };

  const onClickReroute = async () => {
    try {
      await dispatch(rerouteOrderThunk(order?.id));
    } catch (err) {
      console.log(err);
    }
  };

  const onChangeAcceptForTime = (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const dateNotInUtc = createDateTimeFromIso(evt.target.value, getTimezoneForOrderCell(order));
    const dateInUtc = dateNotInUtc.setZone('utc').toJSDate();
    expectedDeliveryDateRef.current = removeSecondsFromIsoString(dateInUtc.toISOString());
  };



  const getProviderLogo = (providerName: string): string | null => {
    switch (providerName?.toUpperCase()) {
      case 'FLIPDISH':
      case 'MANNA':
        return MannaProviderLogo;
      case 'JUSTEAT':
        return JustEatProviderLogo;
      case 'DELIVEROO':
        return DeliverooProviderLogo;
      case 'DOORDASH':
        return DoorDashProviderLogo;
      case 'WOLT':
        return WoltProviderLogo;
      default:
        return null;
    }
  }

  const copyBasketItemsToClipboard = async () => {
    if (!order.basket?.items) {
      notify({ variant: 'error', message: 'No items to copy' });
      return;
    }

    try {
      const formattedItems = order.basket.items.map((item: any) => {
        let itemText = `${item.numberOfItems}x ${item.name} - €${item.unitCost.toFixed(2)} each`;
        
        // Calculate total price for this item
        let itemTotal = item.unitCost * item.numberOfItems;
        
        // Add item options if they exist
        if (item.itemOptions && item.itemOptions.length > 0) {
          const optionsText = item.itemOptions.map((option: any) => {
            itemTotal += option.unitCost;
            return option.unitCost > 0 
              ? `  • ${option.name} (+€${option.unitCost.toFixed(2)})`
              : `  • ${option.name}`;
          }).join('\n');
          itemText += `\n${optionsText}`;
        }
        
        itemText += ` = €${itemTotal.toFixed(2)}`;
        return itemText;
      }).join('\n\n');

      const orderHeader = `Order ${shortOrderId} - ${order?.outletName || order?.externalProviderName || 'MANNA'}`;
      const deliveryInfo = `Delivery to: ${order.eircode}`;
      const totalText = `\nTotal: €${total}`;
      
      const fullText = `${orderHeader}\n${deliveryInfo}\n\nItems:\n${formattedItems}${totalText}`;
      
      await navigator.clipboard.writeText(fullText);
      notify({ variant: 'success', message: 'Basket items copied to clipboard!' });
    } catch (err) {
      notify({ variant: 'error', message: 'Failed to copy to clipboard' });
    }
  }


  const forceOrderEircode = async () => {
    try {
      await dispatch(forceOrderEircodeThunk(order?.id, newEircode));
      setActionToConfirm(null);
    } catch (err) {
      notify({ variant: 'error', message: 'Failed to send messages' });
    }
  };

  const completeMissionConfirm = async () => {
    try {
      await dispatch(completeMissionAirspace(order?.missionId));
      setActionToConfirm(null);
    } catch (err) {
      notify({ variant: 'error', message: 'Failed to send messages' });
    }
  };

  const onFinishChangeAcceptedForTime = () => {
    const newExpectedDeliveryDate = createDateTimeFromIso(expectedDeliveryDateRef.current, 'utc').toJSDate();
    const updatedOrder = {
      ...order,
      expectedDeliveryDate: newExpectedDeliveryDate.toISOString()
    };

    logToDatadog('update_order onFinishChangeAcceptedForTime', {updateOrder, order, newExpectedDeliveryDate: newExpectedDeliveryDate.toISOString()});
    updateOrder(updatedOrder);
  };

  const activeStep = orderStatusSequence.indexOf(order?.status);

  let total =
    order?.basket?.totalCost +
    order?.deliveryCharge +
    order?.basket?.processingFee;
  if (order?.voucher) {
    total = total + order?.voucher?.amount;
  }
  total = total.toFixed(2);

  const showRemoveExtraDeliveryButton = () => {
    if (order && 
        isExtraDeliveryOrder(order) &&
        !NOT_IN_PROGRESS_STATUSES.includes(order.status)) {
      return true;
    }

    return false;
  }

  // Helper function to determine required actions
  // NOTE: We use selectedOrder instead of order because orderDetails (which populates order) 
  // comes from a different API endpoint that doesn't include all properties we need for 
  // action conditions, specifically "needsDropLocationReview" and other properties
  // that are available in selectedOrder but not in the detailed order fetch
  const getRequiredActions = () => {
    const baseActions = getRequiredActionsForOrder(selectedOrder);
    
    // Map to add action handlers specific to OrderDetails
    return baseActions.map(action => ({
      ...action,
      action: () => {
        switch (action.id) {
          case 'confirm-route':
            onOpenRouteConfirmation(order);
            break;
          case 'assign-pad':
            setShowPadSelector(!showPadSelector);
            break;
          case 'suggest-delivery-spot':
            openSuggestModal();
            break;
    }
      }
    }));
  };

  // UI Render Functions
  const renderConfirmationModal = () => (
      <Modal
        open={!!actionToConfirm}
        onClose={() => {
          setActionToConfirm(null);
        }}>
        <Paper sx={{
          position: 'absolute',
          top: '40%',
          left: '50%',
          transform: 'translate(-50%, -40%)',
          width: '50%',
          maxWidth: 2500,
          padding: '15px 10px',
          borderRadius: 1,
          backgroundColor: 'white',
          boxShadow: 24,
          p: 4
        }}>
          <Grid
            spacing={1}
            container
            direction={'column'}
            alignItems="center">
            {actionToConfirm === 'EIRCODE' && (
              <>
                <Grid item>
                  <h3 className={'text-lg font-semibold text-gray-800'}>Confirm Status Update</h3>
                </Grid>
                <Grid item>
                  <Typography variant="h5">{`EIRCODE ${order?.eircode} to ${newEircode}`}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    endIcon={
                      isFetchingForceEircode ? <CircularProgress /> : null
                    }
                    style={{ width: '100%' }}
                    sx={{
                      color: red[500],
                      border: `1px solid ${red[500]}`,
                      backgroundColor: 'white',
                      '&:hover': {
                        border: `1px solid ${red[600]}`
                      }
                    }}
                    onClick={forceOrderEircode}>
                    CONFIRM
                  </Button>
                </Grid>
              </>
            )}
            {actionToConfirm === 'AIRSPACE' && (
              <>
                <Grid item>
                  <h3 className={'text-lg font-semibold text-gray-800'}>Confirm Clear Airspace</h3>
                </Grid>
                <Grid item>
                  <Typography variant="body1">
                    This should only be done if the aircraft has landed as it
                    will clear the airspace block for the selected mission.
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'red',
                      fontWeight: 'bold'
                    }}>{`Set mission status to COMPLETED and clear airspace?`}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    disabled={isCompletingMission}
                    endIcon={isCompletingMission ? <CircularProgress /> : null}
                    style={{ width: '100%' }}
                    sx={{
                      color: red[500],
                      border: `1px solid ${red[500]}`,
                      backgroundColor: 'white',
                      '&:hover': {
                        border: `1px solid ${red[600]}`
                      }
                    }}
                    onClick={completeMissionConfirm}>
                    CONFIRM
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      </Modal>
  );

  const copyOrderIdToClipboard = async () => {
    if (!order?.id) {
      notify({ variant: 'error', message: 'No order ID to copy' });
      return;
    }

    try {
      await navigator.clipboard.writeText(order.id);
      notify({ variant: 'success', message: 'Full order ID copied to clipboard!' });
    } catch (err) {
      notify({ variant: 'error', message: 'Failed to copy to clipboard' });
    }
  };

  const handleOrderIdClick = (event: React.MouseEvent<HTMLElement>) => {
    setOrderIdDropdownAnchor(event.currentTarget);
  };

  const handleOrderIdDropdownClose = () => {
    setOrderIdDropdownAnchor(null);
  };

  const copyToClipboard = async (text: string, description: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notify({ variant: 'success', message: `${description} copied to clipboard` });
      handleOrderIdDropdownClose();
    } catch (err) {
      notify({ variant: 'error', message: `Failed to copy ${description}` });
    }
  };

  // Get unique short order IDs
  const getUniqueShortOrderIds = () => {
    const shortIdFromSelectedOrder = selectedOrder ? getShortIdForOrder(selectedOrder) : null;
    const shortIdFromOrder = order ? getShortIdForOrder(order) : null;
    
    if (!shortIdFromSelectedOrder || !shortIdFromOrder) {
      return shortIdFromSelectedOrder ?? shortIdFromOrder ? [shortIdFromSelectedOrder ?? shortIdFromOrder] : [];
    }
    
    if (shortIdFromSelectedOrder === shortIdFromOrder) {
      return [shortIdFromSelectedOrder];
    }
    
    return [shortIdFromSelectedOrder, shortIdFromOrder];
  };

  // Get text description for drop point status
  const getDropPointStatusText = (status: string): string => {
    switch (status) {
      case 'READY':
        return 'Drop point ready';
      case 'PENDING_REVIEW':
        return 'Pending review';
      case 'PENDING':
      default:
        return 'Checking drop point';
    }
  };

  // Landing zone dropdown data with sorting
  const selectedLandingZoneId = (selectedOrder as any)?.landingZoneId as string | undefined;
  const dropdownLandingZones = useMemo(() => {
    const zones = (deprecatedOrderCardProps?.landingZones && deprecatedOrderCardProps.landingZones.length > 0)
      ? deprecatedOrderCardProps.landingZones
      : selectedOrder?.landingZoneName ? [{ id: selectedLandingZoneId, name: selectedOrder.landingZoneName }] : [];

    return zones.slice().sort((a: any, b: any) => {
      const aSelectedLZ = a?.id === selectedLandingZoneId;
      const bSelectedLZ = b?.id === selectedLandingZoneId;
      if (aSelectedLZ && !bSelectedLZ) return -1;
      if (bSelectedLZ && !aSelectedLZ) return 1;
      return (a?.name || '').localeCompare(b?.name || '');
    });
  }, [deprecatedOrderCardProps?.landingZones, selectedLandingZoneId, selectedOrder?.landingZoneName]);

  // Landing zone selection handler
  const handleSelectLandingZone = useCallback(
    (zoneId: string) => (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (zoneId && zoneId !== selectedLandingZoneId && deprecatedOrderCardProps?.updateLandingZoneForOrder && selectedOrder?.id) {
        deprecatedOrderCardProps.updateLandingZoneForOrder(selectedOrder.id, zoneId);
        popupStateLandingZone.close();
      }
    },
    [deprecatedOrderCardProps?.updateLandingZoneForOrder, selectedOrder?.id, selectedLandingZoneId]
  );

  // Stop event propagation helper
  const stop = (func: (event: SyntheticEvent<any>) => void) => (
    event: SyntheticEvent<any>
  ) => {
    func(event);
    event.stopPropagation();
  };

  const renderHeader = () => (
    <div style={{ padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
      {pos?.logo ? (
        <img alt="store logo" src={pos.logo} style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover' }} />
      ) : (
        <div style={{ width: 24, height: 24 }} />
      )}
      <Typography 
        variant="h5" 
                        style={{
          fontWeight: 600, 
          fontSize: 20,
          cursor: 'pointer',
          padding: '4px 8px',
          borderRadius: '4px',
          transition: 'background-color 0.2s ease',
          userSelect: 'none',
                          display: 'flex',
          alignItems: 'center',
          gap: 4
        }}
        onClick={handleOrderIdClick}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(25, 118, 210, 0.08)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {shortOrderId}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
                          style={{
            transform: orderIdDropdownAnchor ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}
        >
          <path
            d="M6 9L12 15L18 9"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Typography>
      {order?.status && !['DELIVERED', 'COMPLETE'].includes(order.status) && (
        <HeaderTimer expectedDeliveryDate={order?.expectedDeliveryDate} />
      )}
      <div style={{ marginLeft: '16px' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={showDeprecatedCard}
              onChange={(e) => setShowDeprecatedCard(e.target.checked)}
              size="small"
            />
          }
          label="Show old order card"
          sx={{ 
            '& .MuiFormControlLabel-label': { 
              fontSize: '0.875rem',
              whiteSpace: 'nowrap' 
            } 
          }}
        />
      </div>
    </div>
  );

  const renderActivityColumn = () => (
    <Grid
      item
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        order: 3,
        minWidth: 130,
        width: '18%',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '8px 0 12px 0' }}>
        <Typography variant="h4" style={{ fontWeight: 800 }}>
          Activity
        </Typography>
      </div>
      <div
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: 8,
          background: 'white',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ flex: 1, overflow: 'auto' }}>
          <OrderActivityTimeline
            order={order as Order}
            history={orderHistory || []}
            isLoading={isFetchingOrderHistory}
            onOpenExpectedTimetable={() => setShowExpectedTimetableModal(true)}
            onOpenRouteConfirmation={onOpenRouteConfirmation}
            openSuggestModal={openSuggestModal}
            onAssignPad={(e) => {
              e.stopPropagation();
              setShowPadSelector(true);
            }}
            linkedOrdersForOverweightOrders={linkedOrdersForOverweightOrders}
          />
        </div>
      </div>
    </Grid>
  );

  const renderExpectedTimetableModal = () => {
    return (
      <Modal
        open={showExpectedTimetableModal}
        onClose={() => setShowExpectedTimetableModal(false)}
      >
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(980px, 94vw)',
            height: '88vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            borderRadius: 2,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Expected timetable
            </Typography>
            <Button variant="text" onClick={() => setShowExpectedTimetableModal(false)} sx={{ textTransform: 'none' }}>
              Close
            </Button>
          </div>
          <Divider sx={{ my: 1 }} />

          <Box sx={{ pt: 1, flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {expectedOrderTime === null ? (
              <Typography variant="body2" color="textSecondary">
                No expected timetable data available for this order.
              </Typography>
            ) : selectedOrderId ? (
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {isFetchingExpectedOrderTime && expectedOrderTime === undefined ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                    <CircularProgress size={22} />
                  </div>
                ) : null}
                <ExpectedTimetableConnected
                  orderId={selectedOrderId}
                  orderHistory={orderHistory || []}
                  timezone={getTimezoneForOrderCell(order as any)}
                />
              </div>
            ) : null}
          </Box>
        </Paper>
      </Modal>
    );
  };

  const renderOrderColumn = () => (
    <Grid item style={{ height: '100%', display: 'flex', flexDirection: 'column', order: 0, minWidth: 140, width: '20%' }}>
      <Typography variant="h4" style={{ fontWeight: 800, margin: '8px 0 12px 0' }}>Order</Typography>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 8, background: 'white', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ overflow: 'auto', flex: 1 }}>
      
      {/* Merchant */}
      <div style={{ marginBottom: '10px', paddingLeft: '8px', paddingRight: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {pos?.logo ? (
              <img alt="store logo" src={pos.logo} style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover' }} />
            ) : null}
          </div>
          <div style={{ flex: 1 }}>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, marginBottom: 2 }}>Merchant</Typography>
            <Typography variant="body1" style={{ fontSize: 14, fontWeight: 400 }}>
              {order?.outletName || order?.externalProviderName || '—'}
                    </Typography>
          </div>
        </div>
      </div>

      <div 
        data-status-button="true"
        style={{ 
          border: '1px solid #E5E7EB', 
          borderRadius: '8px', 
          padding: '6px 8px', 
          marginBottom: '10px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          backgroundColor: showStatusDropdown ? '#f8fafc' : 'white',
          position: 'relative'
        }}
        onClick={() => setShowStatusDropdown(!showStatusDropdown)}
        onMouseEnter={(e) => {
          if (!showStatusDropdown) {
            e.currentTarget.style.backgroundColor = '#f8fafc';
            e.currentTarget.style.borderColor = '#cbd5e1';
          }
        }}
        onMouseLeave={(e) => {
          if (!showStatusDropdown) {
            e.currentTarget.style.backgroundColor = 'white';
            e.currentTarget.style.borderColor = '#E5E7EB';
          }
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#F2F4F7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <img src={orderDetailsStatusIcon} alt="Status" style={{ width: 20, height: 20 }} />
          </div>
          <div style={{ flex: 1 }}>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, marginBottom: 2 }}>Status</Typography>
            <Typography variant="body1" style={{ fontSize: 14, fontWeight: 400 }}>
                          {orderStatuses[order.status].name}
            </Typography>
                        </div>
          <ChevronRightIcon 
            style={{ 
              fontSize: 18, 
              color: '#64748b',
              transform: showStatusDropdown ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }} 
          />
                      </div>
        
        {/* Status Dropdown */}
        {showStatusDropdown && (
          <div 
            data-status-dropdown="true"
            style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
                        backgroundColor: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            marginTop: '4px',
            maxHeight: '300px',
            overflowY: 'auto'
          }}>
            {orderStatusSequence.map((status, index) => {
              const isCurrentStatus = activeStep === index;
              const isCompletedStatus = activeStep > index;
              // Forward-only progression: NEW is always available, current status, and next status only
              const isNewStatus = index === 0; // First status is always NEW
              const isNextStatus = index === activeStep + 1;
              const isAvailableStatus = isNewStatus || isCurrentStatus || isNextStatus;
              
              // Skip certain statuses for unavailable orders (same logic as original)
              if (order.status === 'UNAVAILABLE' ||
                  order.status === 'REJECTED' ||
                  order.status === 'CANCELLED' ||
                  order.status === 'COMPLETE' ||
                  order.status === 'COMPLETE_BYCAR') {
                return null;
              }
              
              return (
                <div
                  key={`dropdown-status-${status}-${index}`}
                  style={{
                    padding: '12px 16px',
                    borderBottom: index < orderStatusSequence.length - 1 ? '1px solid #f1f5f9' : 'none',
                    cursor: loading ? 'not-allowed' : (isAvailableStatus ? 'pointer' : 'default'),
                    backgroundColor: isCurrentStatus ? '#f0f9ff' : 'transparent',
                    opacity: isAvailableStatus ? 1 : 0.5,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                  onClick={async () => {
                    if (!loading && isAvailableStatus) {
                      setLoading(true);
                      setShowStatusDropdown(false);
                      if (status === 'READY') {
                        onOpenRouteConfirmation(order);
                      } else if (status === 'ACCEPTED') {
                        // Call the same function as clicking "Accept" button with no delay
                        await acceptedOrder(0);
                      } else {
                        await setOrderStatus(order, status);
                      }
                      setLoading(false);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (isAvailableStatus && !loading) {
                      e.currentTarget.style.backgroundColor = isCurrentStatus ? '#e0f2fe' : '#f8fafc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isAvailableStatus) {
                      e.currentTarget.style.backgroundColor = isCurrentStatus ? '#f0f9ff' : 'transparent';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {/* Radio button style indicator */}
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      backgroundColor: isAvailableStatus ? 'white' : '#f3f4f6',
                      border: isAvailableStatus ? '1.5px solid #9ca3af' : '1.5px solid #e5e7eb',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {isCurrentStatus && (
                        <div style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: '#3b82f6'
                        }} />
                      )}
                    </div>
                    
                  <div>
                      <Typography variant="body2" style={{ 
                        fontSize: '14px', 
                        fontWeight: isCurrentStatus ? 600 : 400,
                        color: isAvailableStatus ? '#1f2937' : '#9ca3af'
                      }}>
                        {orderStatuses[status].name}
                      </Typography>
                      {isCurrentStatus && (
                        <Typography variant="caption" style={{ 
                          fontSize: '12px', 
                          color: '#6b7280',
                          display: 'block'
                        }}>
                          {tsToTimeDay(order.modifyDate, getTimezoneForOrderCell(order))}
                        </Typography>
                      )}
                      {activeStep === index - 1 && loading && (
                        <Typography variant="caption" style={{ 
                          fontSize: '12px', 
                          color: '#f59e0b',
                          display: 'block'
                        }}>
                          Updating...
                        </Typography>
                      )}
                  </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Takeoff Time */}
      <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '6px 8px', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#F2F4F7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 16 }}>✈️</span>
          </div>
          <div style={{ flex: 1 }}>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, marginBottom: 2 }}>Takeoff at</Typography>
            <Typography variant="body1" style={{ fontSize: 14, fontWeight: 600 }}>
              {getTakeOffTimeForOrder(order.expectedDeliveryDate)}{' '}
              <Typography component="span" variant="body2" color="textSecondary" style={{ fontSize: 13, fontWeight: 400 }}>
                {getDateForOrderSlot(order.expectedDeliveryDate)}
              </Typography>
            </Typography>
          </div>
        </div>
      </div>

      <div 
        style={{ 
          border: '1px solid #E5E7EB', 
          borderRadius: '8px', 
          padding: '6px 8px', 
          marginBottom: '10px',
          cursor: isEditingExpectedTime ? 'default' : 'pointer',
          transition: 'all 0.2s ease'
        }}
        onClick={() => !isEditingExpectedTime && setIsEditingExpectedTime(true)}
        onMouseEnter={(e) => {
          if (!isEditingExpectedTime) {
            e.currentTarget.style.backgroundColor = '#f8fafc';
          }
        }}
        onMouseLeave={(e) => {
          if (!isEditingExpectedTime) {
            e.currentTarget.style.backgroundColor = 'white';
          }
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#F2F4F7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <img src={orderDetailsExpectedForIcon} alt="Expected For" style={{ width: 20, height: 20 }} />
          </div>
          <div style={{ flex: 1 }} onClick={(e) => isEditingExpectedTime && e.stopPropagation()}>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, marginBottom: 2 }}>Expected For Time</Typography>
            {isEditingExpectedTime ? (
              <TextField
                type="datetime-local"
                defaultValue={isoStringToDateTimeObject(order.expectedDeliveryDate, getTimezoneForOrderCell(order)).toFormat("yyyy-MM-dd'T'HH:mm")}
                onChange={onChangeAcceptForTime}
                onBlur={() => {
                  if (expectedDeliveryDateRef.current) {
                    onFinishChangeAcceptedForTime();
                  }
                  setIsEditingExpectedTime(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur();
                  }
                  if (e.key === 'Escape') {
                    setIsEditingExpectedTime(false);
                  }
                }}
                autoFocus
                size="small"
                fullWidth
                InputProps={{
                  style: { fontSize: 14, fontWeight: 600 }
                }}
                sx={{
                  maxWidth: '90%',
                  '& .MuiInputBase-root': {
                    padding: 0,
                    maxWidth: '90%'
                  },
                  '& .MuiInputBase-input': {
                    padding: '2px 0',
                    fontSize: 14,
                    fontWeight: 600,
                    maxWidth: '90%'
                  }
                }}
              />
            ) : (
              <Typography variant="body1" style={{ fontSize: 14, fontWeight: 600 }}>
                {convertUtcTimeStringForTimezone(order.expectedDeliveryDate, getTimezoneForOrderCell(order))}{' '}
                <Typography component="span" variant="body2" color="textSecondary" style={{ fontSize: 13, fontWeight: 400 }}>
                  {getDateForOrderSlot(order.expectedDeliveryDate)}
                </Typography>
              </Typography>
            )}
            </div>
          {!isEditingExpectedTime && (
            <ChevronRightIcon 
              style={{ 
                fontSize: 18, 
                color: '#64748b',
                transition: 'transform 0.2s ease'
              }} 
            />
          )}
        </div>
      </div>

      {/* Collector */}
      <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '6px 8px', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#F2F4F7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <img src={orderDetailsCollectorIdIcon} alt="Collector" style={{ width: 20, height: 20 }} />
          </div>
          <div style={{ flex: 1 }}>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, marginBottom: 2 }}>Collector</Typography>
            <Typography variant="body1" style={{ fontSize: 14, fontWeight: 400 }}>
              {order.collectorId || 'Unassigned'}
            </Typography>
          </div>
        </div>
      </div>

      {/* Collector Status */}
      <div style={{ padding: '6px 8px', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#F2F4F7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 16 }}>{getEmojiForCollectorStatus(order.collectorStatus)}</span>
          </div>
          <div style={{ flex: 1 }}>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, marginBottom: 2 }}>Collector Status</Typography>
            <Typography variant="body1" style={{ fontSize: 14, fontWeight: 400 }}>
              {getDescriptionForCollectorStatus(order.collectorStatus, order.collectorId)}
            </Typography>
          </div>
        </div>
      </div>

      {/* Drop Point Status */}
      {selectedOrder?.dropPointStatus && (
        <div style={{ padding: '6px 8px', marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div 
              onClick={(e) => e.stopPropagation()}
              style={{ 
                width: 32, 
                height: 32, 
                borderRadius: '50%', 
                backgroundColor: '#FFFFFF', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexShrink: 0,
                border: '1px solid #14142B'
              }}>
              {getElementForDropPointStatus(selectedOrder)}
            </div>
            <div style={{ flex: 1 }}>
              <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, marginBottom: 2 }}>Drop Point</Typography>
              <Typography variant="body1" style={{ fontSize: 14, fontWeight: 400 }}>
                {getDropPointStatusText(selectedOrder.dropPointStatus)}
              </Typography>
            </div>
          </div>
        </div>
      )}

      {/* Landing Zone */}
      {selectedOrder?.landingZoneName && (
        <div 
          {...bindTrigger(popupStateLandingZone)}
          style={{ 
            border: '1px solid #E5E7EB', 
            borderRadius: '8px', 
            padding: '6px 8px', 
            marginBottom: '10px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f8fafc';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div 
              style={{ 
                width: 32, 
                height: 32, 
                borderRadius: '50%', 
                backgroundColor: getLocationColor(selectedOrder.landingZoneName.substring(0, 3)), 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexShrink: 0,
                border: '1px solid #14142B'
              }}>
              <Typography variant="body2" style={{ color: 'white', fontWeight: 600, fontSize: 12 }}>
                {selectedOrder.landingZoneName.substring(0, 3)}
              </Typography>
            </div>
            <div style={{ flex: 1 }}>
              <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, marginBottom: 2 }}>Landing Zone</Typography>
              <Typography variant="body1" style={{ fontSize: 14, fontWeight: 400 }}>
                {selectedOrder.landingZoneName}
              </Typography>
            </div>
            <ChevronRightIcon 
              style={{ 
                fontSize: 18, 
                color: '#64748b',
                transition: 'transform 0.2s ease'
              }} 
            />
          </div>
        </div>
      )}

      {/* Weight */}
      <div style={{ marginBottom: '10px', paddingLeft: '8px', paddingRight: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#F2F4F7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <img src={orderDetailsWeightIcon} alt="Weight" style={{ width: 20, height: 20 }} />
          </div>
          <div style={{ flex: 1 }}>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, marginBottom: 2 }}>Weight</Typography>
            <Typography variant="body1" style={{ fontSize: 14, fontWeight: 400 }}>
              {order?.actualWeight
                ? `${order.actualWeight} kg`
                : (order as any)?.totalWeight
                ? `${((order as any)?.totalWeight / 1000).toFixed(1)} kg`
                : '—'}
            </Typography>
          </div>
        </div>
      </div>

      {linkedOrdersForOverweightOrders?.linkedOverweightOrderIds?.length ? (
        <div style={{ marginBottom: '10px', paddingLeft: '8px', paddingRight: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#F2F4F7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <img src={orderDetailsLinkIcon} alt="Linked" style={{ width: 20, height: 20 }} />
            </div>
            <div style={{ flex: 1 }}>
              <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, marginBottom: 4 }}>Linked orders</Typography>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {linkedOrdersForOverweightOrders.linkedOverweightOrderIds.map((id: string) => {
                  const shortId = id;
                return (
                    <div key={`linked-${id}`} style={{
                      padding: '3px 8px',
                      border: '1px solid #D9DBE9',
                      borderRadius: 10,
                      fontWeight: 400,
                      fontSize: 14
                    }}>
                      {shortId}
                    </div>
                );
              })}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div style={{ marginBottom: '10px', paddingLeft: '8px', paddingRight: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {getProviderLogo(order.externalProviderName || 'MANNA') ? (
              <img 
                alt="provider logo" 
                src={getProviderLogo(order.externalProviderName || 'MANNA')} 
                style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover' }} 
              />
            ) : null}
          </div>
          <div style={{ flex: 1 }}>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, marginBottom: 2 }}>External Provider</Typography>
            <Typography variant="body1" style={{ fontSize: 14, fontWeight: 400 }}>
              {order.externalProviderName || 'MANNA'}
                    </Typography>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '10px', paddingLeft: '8px', paddingRight: '8px' }}>
        <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, marginBottom: 2 }}>External Order Id</Typography>
        <Typography variant="body1" style={{ fontSize: 14, fontWeight: 400 }}>
          {order.externalOrderId || '-'}
                    </Typography>
      </div>

      {/* Divider before Items section */}
      <div style={{ marginTop: '6px', marginBottom: '6px', paddingLeft: '8px', paddingRight: '8px' }}>
        <div style={{ borderBottom: '1px solid #F2F4F7' }} />
      </div>
      
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', marginTop: '16px' }}>
        <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
          Items
                    </Typography>
        <Tooltip title="Copy basket to clipboard">
          <IconButton
            size="small"
            onClick={copyBasketItemsToClipboard}
            sx={{
              color: '#1976d2',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)'
              },
              padding: '4px'
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
      {order.basket?.items?.map((item, i) => (
        <OrderBasketItem key={`order-item-${i}`} item={item} />
      ))}
      
      <Divider style={{ margin: '16px 0' }} />
      
      {/* Voucher Code Display */}
      {order?.voucher && (
        <div style={{ marginBottom: '12px' }}>
          <Typography variant="body2" color="textSecondary">
            Voucher Applied
          </Typography>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '4px'
          }}>
            <Typography variant="body1" style={{ 
              fontWeight: 'bold',
              fontSize: '16px'
            }}>
              {(order.voucher as any)?.code || (order.voucher as any)?.voucherCode || 'VOUCHER'}
            </Typography>
            {order.voucher.amount && (
              <Typography variant="body1" style={{ 
                fontWeight: 'bold',
                fontSize: '16px'
              }}>
                -€{Math.abs(order.voucher.amount).toFixed(2)}
              </Typography>
            )}
          </div>
        </div>
      )}
      
      <div style={{ marginBottom: '8px' }}>
        <Typography variant="body2" color="textSecondary">Total</Typography>
        <Typography variant="h6" style={{ fontWeight: 'bold', marginTop: '4px' }}>
                      € {total}
                    </Typography>
                        </div>
                      </div>
                    </div>
                  </Grid>
  );

  const renderNotesColumn = () => {
    const handleNotesClick = (event: React.MouseEvent<HTMLElement>) => {
      setNotesAnchorEl(event.currentTarget);
    };

    const handleNotesClose = () => {
      setNotesAnchorEl(null);
    };

    const open = Boolean(notesAnchorEl);

    const handleNoteSelect = (noteText: string) => {
      setNote(noteText);
      setNotesAnchorEl(null);
      // Automatically add the note to the order
      addNote(noteText, order?.id);
      // Clear the input field after adding the note
      setNote('');
    };

    return (
      <Grid item style={{ height: '100%', display: 'flex', flexDirection: 'column', order: 4, minWidth: 140, width: '20%' }}>
        <Typography variant="h4" style={{ fontWeight: 800, margin: '8px 0 12px 0' }}>Notes</Typography>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 8, background: 'white', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              flex: 1,
              overflowY: 'auto',
              marginBottom: '8px'
            }}>
              {order?.notes?.length > 0 ? (
                <>
                  {order?.notes
                    ?.slice()
                    ?.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                    ?.map((note, idx, sortedNotes) => (
                    <div key={idx} style={{ 
                      fontSize: '14px',
                      fontFamily: 'Outfit, sans-serif',
                      marginBottom: '8px',
                      paddingBottom: '8px',
                      borderBottom: idx < sortedNotes.length - 1 ? '1px solid #eee' : 'none'
                    }}>
                      <div style={{ 
                        fontSize: '12px',
                        fontFamily: 'Outfit, sans-serif',
                        color: '#999',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {tsToTimeDay(note.timestamp, getTimezoneForOrderCell(order))}
                      </div>
                      <div style={{ 
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        fontFamily: 'Outfit, sans-serif'
                      }}>
                        {note.note.replace(/<[^>]*>/g, '')}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div style={{ fontSize: '14px', fontFamily: 'Outfit, sans-serif', marginBottom: '8px', paddingBottom: '8px' }}>
                  No notes
                </div>
              )}
            </div>
            <div style={{ 
              border: '1px solid #ddd', 
              borderRadius: '8px',
              padding: '8px',
              backgroundColor: '#F7F7FC',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}>
              <input
                type="text"
                style={{ 
                  width: '100%',
                  border: 'none',
                  outline: 'none',
                  fontSize: '14px',
                  fontFamily: 'Outfit, sans-serif',
                  backgroundColor: 'transparent',
                  padding: '0',
                  marginBottom: '2px'
                }}
                placeholder="Add note..."
                value={note}
                onChange={evt => setNote(evt.target.value)}
                onKeyPress={evt => {
                  if (evt.key === 'Enter') {
                    handleAddNote();
                  }
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <IconButton
                  onClick={handleNotesClick}
                          size="small"
                  sx={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: '4px',
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor: '#E5E7EB',
                      borderRadius: '4px'
                    },
                    padding: '2px',
                    minWidth: '20px',
                    minHeight: '20px'
                  }}
                >
                  <img 
                    src={orderDetailsNewNoteIcon} 
                    alt="Add note" 
                    style={{ 
                      width: '14px', 
                      height: '14px'
                    }} 
                  />
                </IconButton>
              </div>
            </div>
          </div>
        </div>
        <Menu
          anchorEl={notesAnchorEl}
          open={open}
          onClose={handleNotesClose}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              border: '1px solid #E5E7EB',
              width: '100%',
              maxWidth: '250px',
              '& .MuiMenuItem-root': {
                fontSize: '14px',
                fontFamily: 'Outfit, sans-serif',
                padding: '8px 12px',
                '&:hover': {
                  backgroundColor: '#F2F4F7'
                }
              },
              '& .MuiListItemIcon-root': {
                minWidth: '36px',
                color: '#1976d2'
              }
            }
          }}
        >
          {PRE_GENERATED_NOTES.map((item) => (
            <MenuItem key={item.id} onClick={() => handleNoteSelect(item.text)}>
              <ListItemText primary={item.text} />
            </MenuItem>
          ))}
        </Menu>
      </Grid>
    );
  };

  const renderDeliveryColumn = () => {
    // Calculate route distance if available
    const distance = selectedOrder?.missionKmDistance ? parseFloat((selectedOrder.missionKmDistance as unknown) as string) : null;
    
    return (
    <Grid item style={{ height: '100%', display: 'flex', flexDirection: 'column', order: 2, minWidth: 140, width: '20%' }}>
      <Typography variant="h4" style={{ fontWeight: 800, margin: '8px 0 12px 0' }}>Delivery</Typography>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, background: 'white', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ overflow: 'auto', flex: 1 }}>
      
        {/* Eircode / Location ID */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ fontSize: '14px', color: '#111827', fontWeight: 500, fontFamily: 'Outfit, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {placeContext?.place.friendlyName || order.eircode}
          </div>
          {placeContext?.place && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '2px 8px',
              borderRadius: '9999px',
              fontSize: '12px',
              fontWeight: 500,
              fontFamily: 'Outfit, sans-serif',
              ...(placeContext.place.enabled
                ? { backgroundColor: '#D1FAE5', color: '#065F46' }
                : { backgroundColor: '#FEE2E2', color: '#991B1B' })
            }}>
              {placeContext.place.enabled ? 'Enabled' : 'Disabled'}
            </span>
          )}
        </div>

        {/* Route Distance */}
        {distance && !isNaN(distance) && (
          <>
            <div style={{ marginBottom: '12px' }}>
              <Typography variant="body2" style={{ fontSize: '14px', color: '#111827', fontWeight: 400, fontFamily: 'Outfit, sans-serif' }}>
                Route distance: {distance}km
              </Typography>
            </div>
          </>
        )}

        <div style={{ marginBottom: '12px', borderBottom: '2px solid #F2F4F7' }} />

        {order.originalOrderId && (
          <>
            <div style={{ marginBottom: '12px' }}>
            <Typography variant="body1" style={{ fontSize: '14px', color: '#EF4444', fontWeight: 500, fontFamily: 'Outfit, sans-serif' }}>
                {!inactiveStatuses.includes(order.status) ? `Resending Order` : `Resent Order`}
              </Typography>
              <Typography variant="body2" style={{ fontSize: '12px', color: 'black', fontWeight: 400, fontFamily: 'Outfit, sans-serif' }}>
                Original order ID: {order.originalOrderId.substring(0, 4)}
              </Typography>
            </div>

            <div style={{ marginBottom: '12px', borderBottom: '2px solid #F2F4F7' }} />
          </>
        )}

        {/* Compact Map */}
        {placeContext && (
          <div style={{ marginBottom: '12px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            <PlaceDropPointMap
              placeContext={placeContext}
              selectedDropPointId={placeContext.place.preferredDropPointId || null}
              onDropPointSelect={() => {}}
              height="200px"
              showLegend={false}
              suggestedDropPointLatLon={order?.requestedDropPos}
              clickMode="select"
              newPinLocation={null}
              riskAssessment={dropPointRiskAssessment}
              isLoadingRiskAssessment={!dropPointRiskAssessment && !!placeContext?.place.preferredDropPointId}
              showDeletedPoints={false}
            />
          </div>
        )}

        {/* Delivery spot panel - clickable to open modal */}
        {placeContext && selectedOrder && (
          <div 
            onClick={openSuggestModal}
            style={{ 
              marginBottom: '12px', 
              padding: '12px', 
              border: '1px solid #e5e7eb', 
              borderRadius: '8px', 
              cursor: 'pointer',
              backgroundColor: 'white'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ 
                  width: '32px', 
                  height: '32px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  borderRadius: '50%', 
                  backgroundColor: '#f3f4f6' 
                }}>
                  <img src={orderDetailsEircodeIcon} alt="Delivery spot" style={{ width: '20px', height: '20px' }} />
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>Delivery spot</div>
              </div>
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#9ca3af' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            
            {/* Chips */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
              {(() => {

                if (!selectedOrder) return null;
                // Use selectedOrder (from Redux/SSE) - same mechanism as getRequiredActionsForOrder
                const isSet = isDeliverySpotSet(selectedOrder);
                return (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '2px 8px',
                    borderRadius: '9999px',
                    fontSize: '12px',
                    fontWeight: 500,
                    backgroundColor: isSet ? '#D1FAE5' : '#FEF3C7',
                    color: isSet ? '#065F46' : '#92400E'
                  }}>
                    {isSet ? 'Set' : 'Not set'}
                  </span>
                );
              })()}
              {/* Building Risk Chip */}
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px 8px',
                borderRadius: '9999px',
                fontSize: '12px',
                fontWeight: 500,
                backgroundColor: dropPointRiskAssessment?.buildingRisk === 'HIGH' ? '#FEE2E2' : 
                                 dropPointRiskAssessment?.buildingRisk === 'LOW' ? '#D1FAE5' : '#F3F4F6',
                color: dropPointRiskAssessment?.buildingRisk === 'HIGH' ? '#991B1B' : 
                       dropPointRiskAssessment?.buildingRisk === 'LOW' ? '#065F46' : '#6B7280'
              }}>
                Building Risk: {dropPointRiskAssessment?.buildingRisk === 'HIGH' ? 'High' : 
                               dropPointRiskAssessment?.buildingRisk === 'LOW' ? 'Low' : 'Error'}
              </span>
              {/* Ground Risk Chip */}
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px 8px',
                borderRadius: '9999px',
                fontSize: '12px',
                fontWeight: 500,
                backgroundColor: dropPointRiskAssessment?.groundRisk === 'HIGH' ? '#FEE2E2' : 
                                 dropPointRiskAssessment?.groundRisk === 'LOW' ? '#D1FAE5' : '#F3F4F6',
                color: dropPointRiskAssessment?.groundRisk === 'HIGH' ? '#991B1B' : 
                       dropPointRiskAssessment?.groundRisk === 'LOW' ? '#065F46' : '#6B7280'
              }}>
                Ground Risk: {dropPointRiskAssessment?.groundRisk === 'HIGH' 
                  ? `High${dropPointRiskAssessment?.heightData !== undefined ? ` (${dropPointRiskAssessment.heightData.toFixed(1)}m)` : ''}` 
                  : dropPointRiskAssessment?.groundRisk === 'LOW' ? 'Low' : 'Error'}
              </span>
            </div>
          </div>
        )}

        {/* Place Notes */}
        {placeContext && (
          <div style={{ marginBottom: '12px' }}>
            {/* Notes Header with Add Button */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              marginBottom: '8px' 
            }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, fontSize: '14px' }}>
                Notes
              </Typography>
              <button
                onClick={() => setIsAddingNote(true)}
                disabled={isAddingNote}
                style={{
                  padding: '6px 12px',
                  borderRadius: '8px',
                  backgroundColor: isAddingNote ? '#9ca3af' : '#000',
                  color: 'white',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: isAddingNote ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isAddingNote) {
                    (e.target as HTMLElement).style.backgroundColor = '#374151';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isAddingNote) {
                    (e.target as HTMLElement).style.backgroundColor = '#000';
                  }
                }}
              >
                Add note
              </button>
            </div>
            <PlaceNotes 
              placeContext={placeContext} 
              isNarrow={true}
              showManagementFeatures={true}
              onAddNote={() => setIsAddingNote(true)}
              isAddingNote={isAddingNote}
              noteText={noteText}
              noteType={noteType}
              onNoteTextChange={setNoteText}
              onNoteTypeChange={(type) => setNoteType(type as LocationNote['noteType'])}
              onSaveNote={handleSaveNote}
              onCancelNote={handleCancelNote}
              isSavingNote={isSavingNote}
              noteError={noteError}
            />
          </div>
        )}

      {/* Phone number (reveal-on-click) */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ flex: 1 }}>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, marginBottom: 2 }}>Phone number</Typography>
            <div
              onClick={onClickPhoneNumber}
              title={hasRevealedPhone && getCustomerPhoneNumber() ? 'Click to copy' : 'Click to reveal'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
            >
              {isFetchingPhone ? (
                <CircularProgress size={14} />
              ) : hasRevealedPhone && getCustomerPhoneNumber() ? (
                <Typography variant="body1" style={{ fontSize: 14, fontWeight: 400 }}>
                  {getCustomerPhoneNumber()}
                </Typography>
              ) : (
                <div style={{
                  filter: 'blur(5px)',
                  backgroundColor: '#F2F4F7',
                  borderRadius: 6,
                  padding: '2px 6px',
                  userSelect: 'none'
                }}>
                  <Typography variant="body1" style={{ fontSize: 14, fontWeight: 400 }}>No phone number available</Typography>
                </div>
              )}
            </div>
            {order?.phoneMaskingCode && (
              <Typography variant="body2" color="textSecondary" style={{ fontSize: 13, marginTop: 4 }}>
                Code: {order?.phoneMaskingCode}
              </Typography>
            )}
          </div>
        </div>
      </div>

      {/* Overdue indicator under Eircode - only show before DELIVERED status */}
      {order?.status && !['DELIVERED', 'COMPLETE'].includes(order.status) && (
        <OverdueTimer expectedDeliveryDate={order?.expectedDeliveryDate} />
      )}
        </div>
      </div>
    </Grid>
  );
  };

  const renderRequiredSection = () => {
    const requiredActions = getRequiredActions();
    
    if (requiredActions.length === 0) {
      return null;
    }
    
    return (
      <>
        <Typography variant="subtitle2" color="textSecondary" style={{ marginBottom: '8px' }}>
          Required
        </Typography>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '32px' }}>
          {requiredActions.map((action) => (
            <div
              key={action.id}
              onClick={action.action}
              style={{
                backgroundColor: '#875BF7',
                borderRadius: '12px',
                padding: '12px 16px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: '60px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#7A4FE8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#875BF7';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {action.id === 'confirm-route' && (
                    <img src={confirmRouteActionIcon} alt="Confirm route" style={{ width: 40, height: 40 }} />
                  )}
                  {action.id === 'assign-pad' && (
                    <img src={assignPadActionIcon} alt="Assign pad" style={{ width: 40, height: 40 }} />
                  )}
                  {action.id === 'suggest-delivery-spot' && (
                    <img src={suggestDeliverySpotActionIcon} alt="Suggest delivery spot" style={{ width: 40, height: 40 }} />
                  )}
                </div>
                <div>
                  <Typography variant="body1" style={{ 
                    color: 'white', 
                    fontWeight: 600, 
                    fontSize: '16px'
                  }}>
                    {action.title}
                  </Typography>
                </div>
              </div>
              <ChevronRightIcon style={{ color: 'white', fontSize: '20px' }} />
            </div>
          ))}
                  </div>
                </>
    );
  };

  const renderActionsColumn = () => (
    <Grid item style={{ height: '100%', display: 'flex', flexDirection: 'column', order: 1, minWidth: 140, width: '20%' }}>
      <Typography variant="h4" style={{ fontWeight: 800, margin: '8px 0 12px 0' }}>Actions</Typography>
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, background: 'white', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ overflow: 'auto', flex: 1 }}>
      
      {/* Route Section */}
      <RoutePanel 
        routeStatus={order?.mission?.status}
        originPadLabel={originLandingPad?.name}
        destinationPadLabel={destinationLandingPad?.name}
        style={{ marginBottom: '16px' }}
        onOriginPadClick={(e) => {
          e.stopPropagation();
          bindTrigger(popupStateDeparturePad).onClick(e);
        }}
        onDestinationPadClick={(e) => {
          e.stopPropagation();
          bindTrigger(popupStateArrivalPad).onClick(e);
        }}
      />

      {/* Required section */}
      {renderRequiredSection()}

      {/* Pad Selector */}
      {showPadSelector && selectedOrder && (
        <PadSelector
          order={selectedOrder}
          onClose={() => setShowPadSelector(false)}
          onError={(message) => notify({ variant: 'error', message })}
        />
      )}

      {/* Order related section - moved to middle */}
      <Typography variant="subtitle2" color="textSecondary" style={{ marginTop: '8px' }}>
        Order related
      </Typography>
      
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, 1fr)', 
        gap: '8px 16px', 
        marginBottom: '32px' 
      }}>
        {/* Row 1: Confirm route, Reroute, Extra Delivery */}
        <div 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            padding: '12px', 
            cursor: 'pointer',
            borderRadius: '8px',
            transition: 'background-color 0.2s ease',
          }}
          onClick={() => onOpenRouteConfirmation(order)}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f8fafc';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <img src={confirmRouteIcon} alt="Confirm route" style={{ width: 58, height: 58, marginBottom: '8px' }} />
          <Typography variant="body2" style={{ fontSize: '12px', textAlign: 'center' }}>
            Confirm route
          </Typography>
        </div>

        <div 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            padding: '12px', 
            cursor: 'pointer',
            borderRadius: '8px',
            transition: 'background-color 0.2s ease',
            opacity: order.originLandingPad === null ? 0.5 : 1
          }}
          onClick={() => {
            if (order.originLandingPad !== null) {
              onClickReroute();
            }
          }}
          onMouseEnter={(e) => {
            if (order.originLandingPad !== null) {
              e.currentTarget.style.backgroundColor = '#f8fafc';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <img src={rerouteIcon} alt="Reroute" style={{ width: 58, height: 58, marginBottom: '8px' }} />
          <Typography variant="body2" style={{ fontSize: '12px', textAlign: 'center' }}>
            Reroute
          </Typography>
        </div>

        <div 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            padding: '12px', 
            cursor: 'pointer',
            borderRadius: '8px',
            transition: 'background-color 0.2s ease',
          }}
          onClick={handleExtraDelivery}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f8fafc';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <img src={extraDeliveryIcon} alt="Extra Delivery" style={{ width: 58, height: 58, marginBottom: '8px' }} />
          <Typography variant="body2" style={{ fontSize: '12px', textAlign: 'center' }}>
            Extra Delivery
          </Typography>
                        </div>

        {/* Row 2: Refund (Flipdish refund OR Deliveroo cancel), Reset, Resend */}
        <div 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            padding: '12px', 
            cursor: canCancelViaRefundAction ? 'pointer' : 'not-allowed',
            borderRadius: '8px',
            transition: 'background-color 0.2s ease',
            opacity: canCancelViaRefundAction ? 1 : 0.5
          }}
          onClick={() => {
            if (canCancelViaRefundAction) {
              setConfirmModalState({ type: OrderConfirmableAction.REFUND, open: true });
            }
          }}
          onMouseEnter={(e) => {
            if (canCancelViaRefundAction) {
              e.currentTarget.style.backgroundColor = '#f8fafc';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <img src={refundIcon} alt={refundActionLabel} style={{ width: 58, height: 58, marginBottom: '8px' }} />
          <Typography variant="body2" style={{ fontSize: '12px', textAlign: 'center' }}>
            {refundActionLabel}
          </Typography>
                  </div>

        <div 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            padding: '12px', 
            cursor: 'pointer',
            borderRadius: '8px',
            transition: 'background-color 0.2s ease',
            opacity: order.status === 'RESET' ? 0.5 : 1
          }}
          onClick={() => {
            if (order.status !== 'RESET') {
              setShowResetModal(true);
            }
          }}
          onMouseEnter={(e) => {
            if (order.status !== 'RESET') {
              e.currentTarget.style.backgroundColor = '#f8fafc';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <img src={resetIcon} alt="Reset" style={{ width: 58, height: 58, marginBottom: '8px' }} />
          <Typography variant="body2" style={{ fontSize: '12px', textAlign: 'center' }}>
            Reset
          </Typography>
        </div>

        <div 
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            padding: '12px', 
            cursor: 'pointer',
            borderRadius: '8px',
            transition: 'background-color 0.2s ease',
          }}
          onClick={() => {
            setShowResendModal(true);
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f8fafc';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <img src={resetResendIcon} alt="Resend" style={{ width: 58, height: 58, marginBottom: '8px' }} />
          <Typography variant="body2" style={{ fontSize: '12px', textAlign: 'center' }}>
            Resend
          </Typography>
        </div>
      </div>



      {/* Advanced actions section */}
      <div
        onClick={() => setShowAdvancedActions(prev => !prev)}
        style={{
          marginTop: '40px',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <Typography variant="subtitle2" color="textSecondary">
          Advanced actions
        </Typography>
        <ChevronRightIcon
          style={{
            fontSize: '18px',
            color: '#64748b',
            transform: showAdvancedActions ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}
        />
      </div>

      {showAdvancedActions && (
        <>
          {showRemoveExtraDeliveryButton() && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px', marginBottom: '40px' }}>
                  <Button
                variant="outlined"
                fullWidth
                onClick={handleRemoveExtraDelivery}
              >
                REMOVE EXTRA DELIVERY
                  </Button>
            </div>
          )}

          {/* Force update eircode section */}
          <div style={{ marginBottom: '24px' }}>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px', fontSize: '12px' }}>
              Force update eircode
            </Typography>
            <TextField
              id="new-eircode-input"
              label="New Eircode"
              variant="filled"
              value={newEircode}
              onChange={updateNewEircode}
              fullWidth
              size="small"
              style={{ marginBottom: '8px' }}
                />
                <Button
              disabled={!newEircode}
              fullWidth
              sx={{
                color: red[500],
                border: `1px solid ${red[500]}`,
                backgroundColor: 'white',
                '&:hover': { 
                  border: `1px solid ${red[600]}`,
                  backgroundColor: 'rgba(255, 0, 0, 0.04)'
                }
              }}
              onClick={() => setActionToConfirm('EIRCODE')}
            >
              Force update Eircode
                </Button>
          </div>

          {/* Enable/Disable address section */}
          {placeContext && (
            <div style={{ marginBottom: '24px' }}>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px', fontSize: '12px' }}>
                {placeContext.place.enabled ? 'Disable address' : 'Enable address'}
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px', fontSize: '11px', fontStyle: 'italic' }}>
                {placeContext.place.enabled 
                  ? 'Disabling this address will prevent new orders from being placed to this location.'
                  : 'Enabling this address will allow new orders to be placed to this location.'}
              </Typography>
              <Button
                fullWidth
                disabled={isTogglingPlaceStatus}
                sx={{
                  color: placeContext.place.enabled ? red[500] : 'green',
                  border: `1px solid ${placeContext.place.enabled ? red[500] : 'green'}`,
                  backgroundColor: 'white',
                  '&:hover': { 
                    border: `1px solid ${placeContext.place.enabled ? red[600] : 'darkgreen'}`,
                    backgroundColor: placeContext.place.enabled ? 'rgba(255, 0, 0, 0.04)' : 'rgba(0, 128, 0, 0.04)'
                  },
                  '&:disabled': {
                    opacity: 0.5
                  }
                }}
                onClick={handleTogglePlaceStatus}
              >
                {isTogglingPlaceStatus 
                  ? 'Updating...' 
                  : placeContext.place.enabled 
                    ? 'Disable address' 
                    : 'Enable address'}
              </Button>
            </div>
          )}

          {/* Clear mission airspace section */}
          {order?.missionId && (
            <div>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px', fontSize: '12px' }}>
                Clear mission airspace
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px', fontSize: '11px', fontStyle: 'italic' }}>
                This should only be done if the aircraft has landed as it will clear the airspace block for the selected mission.
              </Typography>
              <Button
                fullWidth
                sx={{
                  color: red[500],
                  border: `1px solid ${red[500]}`,
                  backgroundColor: 'white',
                  '&:hover': { 
                    border: `1px solid ${red[600]}`,
                    backgroundColor: 'rgba(255, 0, 0, 0.04)'
                  }
                }}
                onClick={() => setActionToConfirm('AIRSPACE')}
              >
                Clear mission airspace
              </Button>
            </div>
          )}
        </>
      )}
            </div>
      </div>
    </Grid>
  );



  const renderAcceptButtonsBar = () => {
    if (activeStep !== 0 && !order?.missionId) {
      return null;
    }

    return (
          <div style={{ position: 'absolute', backgroundColor: 'white', width: '100%', bottom: 0 }}>
            <Divider />
            <Grid
              container
              justifyContent="space-between"
              style={{ padding: '10px 20px' }}>
                {activeStep === 0 && (
            <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => acceptedOrder(0)}
                      disabled={acceptingOrderWithEta}
                      sx={{
                        color: green[500],
                        backgroundColor: 'white',
                        border: `1px solid ${green[500]}`,
                        boxShadow: 'none',
                        marginRight: 1,
                        height: 60,
                        '&:hover': {
                          backgroundColor: green[700],
                          color: 'white'
                        }
                      }}
                    >
                      {acceptingOrderWithEta === 0 ? (
                        <CircularProgress size={12} />
                      ) : (
                        'Accept'
                      )}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => acceptedOrder(10)}
                      disabled={acceptingOrderWithEta}
                      sx={{
                        color: green[500],
                        backgroundColor: 'white',
                        border: `1px solid ${green[500]}`,
                        boxShadow: 'none',
                        marginRight: 1,
                        height: 60,
                        '&:hover': {
                          backgroundColor: green[700],
                          color: 'white'
                        }
                      }}
                    >
                      {acceptingOrderWithEta === 10 ? (
                        <CircularProgress size={12} />
                      ) : (
                        'Accept +10 mins'
                      )}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => acceptedOrder(20)}
                      disabled={acceptingOrderWithEta}
                      sx={{
                        color: green[500],
                        backgroundColor: 'white',
                        border: `1px solid ${green[500]}`,
                        boxShadow: 'none',
                        marginRight: 1,
                        height: 60,
                        '&:hover': {
                          backgroundColor: green[700],
                          color: 'white'
                        }
                      }}
                    >
                      {acceptingOrderWithEta === 20 ? (
                        <CircularProgress size={12} />
                      ) : (
                        'Accept +20 mins'
                      )}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => acceptedOrder(40)}
                      disabled={acceptingOrderWithEta}
                      sx={{
                        color: green[500],
                        backgroundColor: 'white',
                        border: `1px solid ${green[500]}`,
                        boxShadow: 'none',
                        marginRight: 1,
                        height: 60,
                        '&:hover': {
                          backgroundColor: green[700],
                          color: 'white'
                        }
                      }}
                    >
                      {acceptingOrderWithEta === 40 ? (
                        <CircularProgress size={12} />
                      ) : (
                        'Accept +40 mins'
                      )}
                    </Button>
                    <ETADialog
                      disabled={false}
                      accepting={
                        acceptingOrderWithEta &&
                        acceptingOrderWithEta !== 1 &&
                        acceptingOrderWithEta !== 10 &&
                        acceptingOrderWithEta !== 20 &&
                        acceptingOrderWithEta !== 40
                      }
                      onAccept={acceptedOrder}
                    />
              </Grid>
                )}
              
              {fdOrder && (
                <Grid item>
                  <RejectCancelDialog
                    isRefund={order.status !== 'CREATED'}
                    order={order}
                    amount={fdOrder.Amount}
                    onReject={rejectOrder}
                    onRefund={refundOrder}
                    rejecting={rejectingOrder}
                    refunding={refundingOrder}
                  />
                </Grid>
              )}
              </Grid>
          </div>
    );
  };

  if (!order) return null;
  
  return (
    <Grid container spacing={1} sx={{ height: '100%' }}>
      {renderConfirmationModal()}
      {renderExpectedTimetableModal()}
      {/* Action Confirmation Modals */}
      {confirmModalState.type && confirmModalState.open && (
        <ActionConfirmationModal
          open={true}
          onClose={() => setConfirmModalState({ type: null, open: false })}
          onConfirm={() => {
            switch (confirmModalState.type) {
              case OrderConfirmableAction.REFUND:
                if (fdOrder) {
                  refundOrder(order, fdOrder.Amount, 'User requested refund');
                } else if (isDeliverooOrder) {
                  // Deliveroo: no refund API call in this app, just cancel the order
                  setOrderStatus(order, 'CANCELLED');
                }
                break;
              case OrderConfirmableAction.RESET:
                handleReset();
                break;
            }
          }}
          title={
            confirmModalState.type === OrderConfirmableAction.REFUND && isDeliverooOrder
              ? 'Cancel order'
              : ORDER_CONFIRMABLE_ACTION_COPY[confirmModalState.type].title
          }
          description={
            confirmModalState.type === OrderConfirmableAction.REFUND && isDeliverooOrder
              ? 'Are you sure you want to cancel this order? This action cannot be undone.'
              : ORDER_CONFIRMABLE_ACTION_COPY[confirmModalState.type].description
          }
          actionText={
            confirmModalState.type === OrderConfirmableAction.REFUND && isDeliverooOrder
              ? 'Cancel'
              : ORDER_CONFIRMABLE_ACTION_COPY[confirmModalState.type].actionText
          }
        />
      )}

      {/* Resend Confirmation Modal */}
      <ResendConfirmationModal
        open={showResendModal}
        onClose={() => setShowResendModal(false)}
        onConfirm={handleResend}
      />

      {/* Reset Confirmation Modal */}
      <ResetConfirmationModal
        open={showResetModal}
        onClose={() => setShowResetModal(false)}
        onConfirm={handleReset}
      />
      <Grid item xs={12}>
        <div style={{ height: '100%', position: 'relative' }}>
          <div style={{ position: 'absolute', height: '100%', width: '100%', overflow: 'auto', paddingBottom: 20 }}>
            {renderHeader()}
            
            {/* Deprecated Order Card - shown when checkbox is toggled */}
            {showDeprecatedCard && selectedOrder && deprecatedOrderCardProps && (
              <div style={{ padding: '0 20px 16px 20px', display: 'flex', justifyContent: 'center' }}>
                <DeprecatedOrderCard
                  key={selectedOrder.id}
                  order={selectedOrder}
                  onOpenRouteConfirmation={onOpenRouteConfirmation}
                  openSuggestModal={openSuggestModal}
                  {...deprecatedOrderCardProps}
                />
              </div>
            )}
            
            {/* 4 Column Layout */}
            <div style={{ padding: '0 20px 5px 20px', height: 'calc(100% - 60px)', overflowX: 'auto' }}>
              <Grid container spacing={3} style={{ minWidth: 1100, height: '100%' }}>
                {renderActivityColumn()}
                {renderOrderColumn()}
                {renderNotesColumn()}
                {renderDeliveryColumn()}
                {renderActionsColumn()}
              </Grid>
            </div>
          </div>
          {renderAcceptButtonsBar()}
        </div>
      </Grid>
      
      {/* Order ID Dropdown Menu */}
      <Menu
        anchorEl={orderIdDropdownAnchor}
        open={Boolean(orderIdDropdownAnchor)}
        onClose={handleOrderIdDropdownClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1,
            minWidth: 240,
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            border: '1px solid rgba(0,0,0,0.05)',
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1.5,
              borderRadius: '6px',
              mx: 0.75,
              my: 0.25,
              fontSize: '14px',
              transition: 'all 0.15s ease',
                      '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
              },
              '&:first-of-type': {
                mt: 0.75,
              },
              '&:last-of-type': {
                mb: 0.75,
              },
            },
            '& .MuiDivider-root': {
              mx: 1.5,
            },
          },
        }}
      >
        {/* Copy Full Order ID */}
        <MenuItem 
          onClick={() => copyToClipboard(order?.id || '', 'Full order ID')}
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <ListItemText 
            primary="Copy full order ID"
            secondary={order?.id}
            primaryTypographyProps={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#1a1a1a',
            }}
            secondaryTypographyProps={{
              fontSize: '12px',
              color: 'text.secondary',
              sx: {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '160px',
              }
            }}
          />
          <ContentCopyIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 1 }} />
        </MenuItem>
        
        <Divider sx={{ my: 0.5 }} />
        
        {/* Copy Short Order ID(s) */}
        {getUniqueShortOrderIds().map((shortId, index) => (
          <MenuItem 
            key={`short-${index}`} 
            onClick={() => copyToClipboard(shortId || '', 'Short order ID')}
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <ListItemText 
              primary={"Copy short order ID"}
              secondary={shortId}
              primaryTypographyProps={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#1a1a1a',
              }}
              secondaryTypographyProps={{
                fontSize: '12px',
                color: 'text.secondary',
              }}
            />
            <ContentCopyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </MenuItem>
        ))}
      </Menu>
      
      {/* Landing Zone Selection Menu */}
      <Menu {...bindMenu(popupStateLandingZone)} onClose={stop(bindMenu(popupStateLandingZone).onClose)}>
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 220, p: 1 }}>
          {dropdownLandingZones.map((zone: any) => {
            const zoneName: string = zone?.name || '';
            const zoneId: string = zone?.id;
            const abbr = (zoneName || '').substring(0, 3);
            const isSelected = zoneId === selectedLandingZoneId;

            return (
              <Box
                key={`lz-option-${zoneId}`}
                onClick={handleSelectLandingZone(zoneId)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1,
                  py: 1,
                  borderRadius: 1,
                  backgroundColor: isSelected ? '#EEF2FF' : 'white',
                  cursor: isSelected ? 'default' : 'pointer',
                  '&:hover': { backgroundColor: isSelected ? '#EEF2FF' : '#F3F4F6' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '9999px',
                    bgcolor: getLocationColor(abbr),
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 12,
                    lineHeight: 1,
                    mr: 1
                  }}>
                    {abbr}
                  </Box>
                  <Typography variant="body2" color="text.primary">{zoneName}</Typography>
                </Box>
                {isSelected ? (
                  <Typography variant="body2" color="primary">✓</Typography>
                ) : null}
              </Box>
            );
          })}
        </Box>
      </Menu>

      {/* Departure Pad Selection Menu */}
      <Menu {...bindMenu(popupStateDeparturePad)} onClose={stop(bindMenu(popupStateDeparturePad).onClose)}>
        <OrderCardContext 
          popupState={popupStateDeparturePad} 
          order={selectedOrder || order} 
          tags={order?.tags} 
          subMenu="aircraft"
          pads={getAllActivePadsForOrder(selectedOrder || order, pads || [])}
        />
      </Menu>

      {/* Arrival Pad Selection Menu */}
      <Menu {...bindMenu(popupStateArrivalPad)} onClose={stop(bindMenu(popupStateArrivalPad).onClose)}>
        <OrderCardContext 
          popupState={popupStateArrivalPad} 
          order={selectedOrder || order} 
          tags={order?.tags} 
          subMenu="arrival-pad"
          pads={getAllActivePadsForOrder(selectedOrder || order, pads || [])}
        />
      </Menu>
    </Grid>
  );
};

const mapStateToProps = state => ({
  acceptingOrderWithEta: state.orders.acceptingOrderWithEta,
  rejectingOrder: state.orders.rejecting,
  refundingOrder: state.orders.refunding,
  timeFilter: state.orders.timeFilter,
});

const mapDispatchToProps = dispatch => ({
  addNote: (note, orderId) => dispatch(addNoteToOrder(note, orderId)),
  resetOrder: (order, reason) => dispatch(resetOrder(order, reason)),
  resendOrder: (order, reason, shouldRemakeFood) => dispatch(resetAndResendOrder(order, reason, shouldRemakeFood)),
  setOrderStatus: (orderId, status) =>
    dispatch(updateOrderStatus(orderId, status)),
  rejectOrder: (order, reason) => dispatch(rejectOrder(order, reason)),
  refundOrder: (order, amount, reason) =>
    dispatch(refundOrder(order, amount, reason)),
  acceptOrder: (order, eta) => dispatch(acceptOrder(order, eta)),
  notify: notification => dispatch(newNotification(notification)),
  updateOrder: (order: Order) => dispatch(updateOrder(order)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderDetails);